import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { dayjs } from "@/utils/date";
import type { Reservation } from "@/store/types";

/** A reservation we already know overlaps the proposed walk-in window
 *  on at least one of the picked mesas, plus its parsed local-instant
 *  start (so callers don't re-parse). */
export interface OverbookClash {
  reservation: Reservation;
  startsAtLocal: dayjs.Dayjs;
}

interface Props {
  open: boolean;
  clashes: OverbookClash[];
  /** Map a table id to its display name (e.g. ``"T9"``). */
  tableNameOf: (id: string) => string;
  /** When the walk-in's duration was auto-shortened so it doesn't
   *  physically overlap the next reservation, show the new end time
   *  in the modal so the operator knows what they're committing to. */
  cappedEndTime?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation modal that gates a walk-in seat when an upcoming
 *  reservation already lays claim to one of the picked mesas. The
 *  amber alert above the submit button is informational; this modal
 *  is the explicit "yes I know, seat anyway" gate so a hurried tap
 *  can't silently overbook. */
export function WalkinOverbookConfirm({
  open,
  clashes,
  tableNameOf,
  cappedEndTime,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Sentar igual?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block">
              {clashes.length === 1
                ? "Hay una reserva próxima sobre esta(s) mesa(s):"
                : `Hay ${clashes.length} reservas próximas sobre esta(s) mesa(s):`}
            </span>
            <ul className="mt-2 flex flex-col gap-0.5 text-foreground">
              {clashes.map(({ reservation, startsAtLocal }) => {
                const minutesAway = Math.round(startsAtLocal.diff(dayjs(), "minute", true));
                const tableNames = reservation.tableIds.map(tableNameOf).join(" + ");
                return (
                  <li key={reservation.id} className="text-sm leading-snug">
                    <strong>{reservation.time}</strong>
                    {" · "}
                    {reservation.guestName || "Sin nombre"} ({reservation.pax}p) · {tableNames}
                    <span className="ml-1 text-muted-foreground">— en {minutesAway} min</span>
                  </li>
                );
              })}
            </ul>
            {cappedEndTime ? (
              <span className="mt-3 block text-xs text-foreground">
                El walk-in se sentará hasta las <strong>{cappedEndTime}</strong> para liberar la
                mesa antes de la próxima reserva.
              </span>
            ) : (
              <span className="mt-3 block text-xs text-muted-foreground">
                Si continúas, podríamos no tener la(s) mesa(s) disponible(s) cuando llegue la
                reserva.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              // ``onConfirm`` is the only real side effect — fire it
              // FIRST so even if the dialog's exit / parent unmount
              // interrupt the rest of the click chain, the seat lands.
              onConfirm();
            }}
          >
            Sentar igual
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
